// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useContainerQuery } from '@cloudscape-design/component-toolkit';
import { getIsRtl, SingleTabStopNavigationProvider, useMergeRefs, useResizeObserver, useSingleTabStopNavigation, } from '@cloudscape-design/component-toolkit/internal';
import { getAnalyticsMetadataAttribute } from '@cloudscape-design/component-toolkit/internal/analytics-metadata';
import { InternalButton } from '../button/internal';
import { useInternalI18n } from '../i18n/context';
import { getAllFocusables } from '../internal/components/focus-lock/utils';
import { hasModifierKeys, isPlainLeftClick } from '../internal/events';
import useHiddenDescription from '../internal/hooks/use-hidden-description';
import { usePrevious } from '../internal/hooks/use-previous';
import { useOneTheme, useVisualRefresh } from '../internal/hooks/use-visual-mode';
import { KeyCode } from '../internal/keycode';
import { circleIndex } from '../internal/utils/circle-index';
import { isHTMLElement } from '../internal/utils/dom';
import handleKey from '../internal/utils/handle-key';
import Tooltip from '../tooltip/internal.js';
import { hasHorizontalOverflow, hasInlineEndOverflow, hasInlineStartOverflow, onPaginationClick, scrollIntoView, } from './scroll-utils';
import { getTabContainerStyles, getTabStyles } from './styles';
import analyticsSelectors from './analytics-metadata/styles.css.js';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
const tabSelector = `.${styles['tabs-tab-link']}`;
const focusedTabSelector = `.${styles['tabs-tab-focused']}`;
const focusableTabSelector = `.${styles['tabs-tab-focusable']}`;
function dismissButton({ dismissLabel, dismissDisabled, onDismiss, tabId, }) {
    return (React.createElement(InternalButton, { onClick: onDismiss, variant: "icon", iconName: "close", formAction: "none", ariaLabel: dismissLabel, disabled: dismissDisabled, className: clsx(testUtilStyles['tab-dismiss-button'], analyticsSelectors['tab-dismiss-button']), "data-testid": `awsui-tab-dismiss-button-${tabId}` }));
}
export function TabHeaderBar({ onChange, activeTabId, tabs, variant, idNamespace, ariaLabel, ariaLabelledby, i18nStrings, keyboardActivationMode, actions, style, }) {
    const headerBarRef = useRef(null);
    const activeTabHeaderRef = useRef(null);
    const inlineStartOverflowButton = useRef(null);
    const i18n = useInternalI18n('tabs');
    const isVisualRefresh = useVisualRefresh();
    const isOneTheme = useOneTheme();
    const [indicatorStyle, setIndicatorStyle] = useState({ opacity: 0 });
    const previousIndicatorTabId = usePrevious(activeTabId);
    const activeHeaderContainerRef = useRef(null);
    const containerObjectRef = useRef(null);
    const documentRef = useRef(typeof document !== 'undefined' ? document : null);
    const documentRefCallback = (node) => { var _a; return (documentRef.current = (_a = node === null || node === void 0 ? void 0 : node.ownerDocument) !== null && _a !== void 0 ? _a : document); };
    const [widthChange, containerMeasureRef] = useContainerQuery(rect => rect.contentBoxWidth);
    const containerRef = useMergeRefs(containerObjectRef, containerMeasureRef, documentRefCallback);
    const tabRefs = useRef(new Map());
    const [horizontalOverflow, setHorizontalOverflow] = useState(false);
    const [inlineStartOverflow, setInlineStartOverflow] = useState(false);
    const [inlineEndOverflow, setInlineEndOverflow] = useState(false);
    const [focusedTabId, setFocusedTabId] = useState(activeTabId);
    const [previousActiveTabId, setPreviousActiveTabId] = useState(activeTabId);
    const hasActionOrDismissible = tabs.some(tab => tab.action || tab.dismissible);
    const hadActionOrDismissible = usePrevious(hasActionOrDismissible);
    const tabActionAttributes = hasActionOrDismissible
        ? {
            role: 'application',
            'aria-roledescription': i18n('i18nStrings.tabsWithActionsAriaRoleDescription', i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.tabsWithActionsAriaRoleDescription),
        }
        : {
            role: 'tablist',
        };
    useEffect(() => {
        var _a;
        if (hadActionOrDismissible && !hasActionOrDismissible) {
            // when tabs becomes non-dismissible (e.g. when all dismissible tabs are removed),
            // the hasActionOrDismissible is changing which causing tabs to re-mount to the React tree,
            // which, in turn, causes losing their refs, and the nextActive.focus() function inside handleDismiss does not focus on the next tab
            // so this code does
            (_a = getNextFocusTarget()) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [hasActionOrDismissible, hadActionOrDismissible]);
    useEffect(() => {
        if (headerBarRef.current) {
            setHorizontalOverflow(hasHorizontalOverflow(headerBarRef.current, inlineStartOverflowButton));
            setInlineStartOverflow(hasInlineStartOverflow(headerBarRef.current));
            setInlineEndOverflow(hasInlineEndOverflow(headerBarRef.current));
        }
    }, [widthChange, tabs]);
    const scrollIntoViewIfPossible = (smooth) => {
        if (!activeTabId) {
            return;
        }
        const activeTabRef = tabRefs.current.get(activeTabId);
        if (activeTabRef && headerBarRef.current) {
            scrollIntoView(activeTabRef, headerBarRef.current, smooth);
        }
    };
    useEffect(() => {
        // Delay scrollIntoView as the position is depending on parent elements
        // (effects are called inside-out in the component tree).
        // Wait one frame to allow parents to complete it's calculation.
        requestAnimationFrame(() => {
            scrollIntoViewIfPossible(false);
        });
        // Non-smooth scrolling should not be called upon activeId change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horizontalOverflow, widthChange, tabs.length]);
    // Keep the roving tab stop in sync with the active tab so that keyboard focus
    // lands on the active tab even when it is selected programmatically.
    useEffect(() => {
        setFocusedTabId(activeTabId);
    }, [activeTabId]);
    useEffect(() => {
        scrollIntoViewIfPossible(true);
        // Smooth scrolling should only be called upon activeId change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTabId]);
    useEffect(() => {
        var _a, _b;
        /*
         When the selected tab changes and we are currently already focused on a tab,
         move the focus to the newly selected tab.
        */
        if (documentRef.current && ((_a = headerBarRef.current) === null || _a === void 0 ? void 0 : _a.contains(documentRef.current.activeElement))) {
            if (documentRef.current.activeElement !== activeTabHeaderRef.current) {
                (_b = activeTabHeaderRef.current) === null || _b === void 0 ? void 0 : _b.focus({ preventScroll: true });
            }
        }
    }, [activeTabId]);
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    const activeTabVisible = activeTab !== undefined && !activeTab.disabled;
    const updateIndicator = () => {
        const list = headerBarRef.current;
        const headerContainer = activeTabVisible ? activeHeaderContainerRef.current : null;
        if (!list || !headerContainer) {
            setIndicatorStyle({ opacity: 0 });
            return;
        }
        const borderWidth = headerContainer.clientLeft;
        // Layout metrics ignore transforms (such as a Modal scaling in) and the scroll position.
        // start = tabStart + border, width = tabWidth - 2 * border - 1 (as the per-tab :after)
        const tabStart = getIsRtl(list)
            ? list.clientWidth - headerContainer.offsetLeft - headerContainer.offsetWidth
            : headerContainer.offsetLeft;
        const insetInlineStart = `${tabStart + borderWidth}px`;
        const inlineSize = `${headerContainer.offsetWidth - 2 * borderWidth - 1}px`;
        const animate = previousIndicatorTabId !== undefined && previousIndicatorTabId !== activeTabId;
        setIndicatorStyle(prev => {
            const sameGeometry = prev.insetInlineStart === insetInlineStart && prev.inlineSize === inlineSize && prev.opacity === 1;
            if (sameGeometry) {
                return prev;
            }
            return { insetInlineStart, inlineSize, opacity: 1, transition: animate ? undefined : 'none' };
        });
    };
    useLayoutEffect(() => {
        if (isOneTheme) {
            updateIndicator();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOneTheme, activeTabId, activeTabVisible, tabs, widthChange]);
    const getActiveHeaderContainer = useCallback(() => (activeTabId !== undefined ? activeHeaderContainerRef.current : null), [activeTabId]);
    useResizeObserver(getActiveHeaderContainer, () => {
        if (isOneTheme) {
            updateIndicator();
        }
    });
    const onScroll = () => {
        if (headerBarRef.current) {
            setInlineStartOverflow(hasInlineStartOverflow(headerBarRef.current));
            setInlineEndOverflow(hasInlineEndOverflow(headerBarRef.current));
        }
    };
    const classes = clsx({
        [styles['tabs-header']]: true,
        [styles['tabs-header-with-divider']]: variant === 'default' || isVisualRefresh,
    });
    const leftButtonClasses = clsx({
        [styles['pagination-button']]: true,
        [styles['pagination-button-left']]: true,
        [styles['pagination-button-left-scrollable']]: inlineStartOverflow,
    });
    const rightButtonClasses = clsx({
        [styles['pagination-button']]: true,
        [styles['pagination-button-right']]: true,
        [styles['pagination-button-right-scrollable']]: inlineEndOverflow,
    });
    const navigationAPI = useRef(null);
    function getNextFocusTarget() {
        var _a, _b;
        if (!containerObjectRef.current) {
            return null;
        }
        const tabElements = Array.from(containerObjectRef.current.querySelectorAll(tabSelector));
        return (_b = (_a = tabElements.find(tab => tab.matches(focusedTabSelector))) !== null && _a !== void 0 ? _a : tabElements.find(tab => !tab.disabled)) !== null && _b !== void 0 ? _b : null;
    }
    function onUnregisterActive(focusableElement) {
        var _a;
        const isFocusableActionOrDismissible = !focusableElement.classList.contains(styles['tabs-tab-link']);
        if (!isFocusableActionOrDismissible) {
            const nextFocusTarget = (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.getFocusTarget();
            const tabLinkButton = nextFocusTarget === null || nextFocusTarget === void 0 ? void 0 : nextFocusTarget.querySelector(`.${styles['tabs-tab-link']}`);
            tabLinkButton === null || tabLinkButton === void 0 ? void 0 : tabLinkButton.focus();
        }
    }
    useEffect(() => {
        var _a;
        (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.updateFocusTarget();
    });
    function onFocus() {
        var _a;
        (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.updateFocusTarget();
    }
    function onBlur() {
        var _a;
        (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.updateFocusTarget();
    }
    function onKeyDown(event) {
        var _a, _b, _c;
        const focusTarget = (_a = documentRef.current) === null || _a === void 0 ? void 0 : _a.activeElement;
        const specialKeys = [
            KeyCode.right,
            KeyCode.left,
            KeyCode.end,
            KeyCode.home,
            KeyCode.pageUp,
            KeyCode.pageDown,
            KeyCode.space,
        ];
        const isActionOpen = (_b = documentRef.current) === null || _b === void 0 ? void 0 : _b.querySelector(`.${styles['tabs-tab-action']} [aria-expanded="true"]`);
        const isDismissOrActionFocused = !(focusTarget === null || focusTarget === void 0 ? void 0 : focusTarget.classList.contains(styles['tabs-tab-link']));
        if (isActionOpen) {
            return;
        }
        if (event.key === 'Tab' && !event.shiftKey && isDismissOrActionFocused) {
            event.preventDefault();
            const panelId = `${idNamespace}-${activeTabId}-panel`;
            const panel = (_c = documentRef.current) === null || _c === void 0 ? void 0 : _c.getElementById(panelId);
            panel === null || panel === void 0 ? void 0 : panel.focus();
        }
        if (hasModifierKeys(event) || specialKeys.indexOf(event.keyCode) === -1) {
            return;
        }
        if (!containerObjectRef.current || !focusTarget) {
            return;
        }
        event.preventDefault();
        const focusables = getFocusablesFrom(containerObjectRef.current);
        const activeIndex = isHTMLElement(focusTarget) ? focusables.indexOf(focusTarget) : -1;
        handleKey(event, {
            onHome: () => focusElement(focusables[0]),
            onEnd: () => focusElement(focusables[focusables.length - 1]),
            onInlineStart: () => focusElement(focusables[circleIndex(activeIndex - 1, [0, focusables.length - 1])]),
            onInlineEnd: () => focusElement(focusables[circleIndex(activeIndex + 1, [0, focusables.length - 1])]),
            onPageDown: () => inlineEndOverflow && onPaginationClick(headerBarRef, 'forward'),
            onPageUp: () => inlineStartOverflow && onPaginationClick(headerBarRef, 'backward'),
            onActivate: () => {
                var _a;
                return focusedTabId &&
                    focusedTabId !== activeTabId &&
                    onChange({ activeTabId: focusedTabId, activeTabHref: (_a = tabs.find(tab => tab.id === focusedTabId)) === null || _a === void 0 ? void 0 : _a.href });
            },
        });
    }
    function focusElement(element) {
        var _a, _b;
        element.focus();
        // If focusable element is a tab - fire the onChange for it.
        const tabsById = tabs.reduce((map, tab) => map.set(tab.id, tab), new Map());
        for (const [tabId, focusTargetTabTriggerElement] of tabRefs.current.entries()) {
            const focusTargetTabLabelElement = focusTargetTabTriggerElement === null || focusTargetTabTriggerElement === void 0 ? void 0 : focusTargetTabTriggerElement.querySelector(`.${styles['tabs-tab-link']}`);
            if (tabId !== activeTabId && focusTargetTabLabelElement === element) {
                setPreviousActiveTabId(tabId);
                setFocusedTabId(tabId);
                if (!((_a = tabsById.get(tabId)) === null || _a === void 0 ? void 0 : _a.disabled) && keyboardActivationMode === 'automatic') {
                    onChange({ activeTabId: tabId, activeTabHref: (_b = tabsById.get(tabId)) === null || _b === void 0 ? void 0 : _b.href });
                }
                break;
            }
        }
    }
    // List all non-disabled and registered focusables: those are eligible for keyboard navigation.
    function getFocusablesFrom(target) {
        function isElementRegistered(element) {
            var _a, _b;
            return (_b = (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.isRegistered(element)) !== null && _b !== void 0 ? _b : false;
        }
        function isElementFocusable(element) {
            if (element instanceof HTMLButtonElement) {
                return !element.disabled || element.closest(focusableTabSelector);
            }
            return element.matches(focusableTabSelector);
        }
        return getAllFocusables(target).filter(el => isElementRegistered(el) && isElementFocusable(el));
    }
    const TabList = hasActionOrDismissible ? 'div' : 'ul';
    const Indicator = hasActionOrDismissible ? 'span' : 'li';
    return (React.createElement("div", { className: classes },
        React.createElement("div", { className: styles['tab-header-scroll-container'], ref: containerRef },
            horizontalOverflow && (React.createElement("span", { ref: inlineStartOverflowButton, className: leftButtonClasses },
                React.createElement(InternalButton, { formAction: "none", variant: "icon", iconName: "angle-left", disabled: !inlineStartOverflow, __focusable: true, onClick: () => onPaginationClick(headerBarRef, 'backward'), ariaLabel: i18n('i18nStrings.scrollLeftAriaLabel', i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.scrollLeftAriaLabel) }))),
            React.createElement(SingleTabStopNavigationProvider, { ref: navigationAPI, navigationActive: true, getNextFocusTarget: getNextFocusTarget, onUnregisterActive: onUnregisterActive },
                React.createElement(TabList, { ...tabActionAttributes, className: clsx(styles['tabs-header-list'], analyticsSelectors['tabs-header-list']), "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, ref: headerBarRef, style: isOneTheme ? getTabContainerStyles(style) : undefined, onScroll: onScroll, onKeyDown: onKeyDown, onFocus: onFocus, onBlur: onBlur },
                    tabs.map(renderTabHeader),
                    isOneTheme && activeTabVisible && (React.createElement(Indicator, { className: styles['tabs-active-indicator'], style: indicatorStyle, "aria-hidden": "true" })))),
            horizontalOverflow && (React.createElement("span", { className: rightButtonClasses },
                React.createElement(InternalButton, { formAction: "none", variant: "icon", iconName: "angle-right", disabled: !inlineEndOverflow, __focusable: true, onClick: () => onPaginationClick(headerBarRef, 'forward'), ariaLabel: i18n('i18nStrings.scrollRightAriaLabel', i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.scrollRightAriaLabel) })))),
        actions && React.createElement("div", { className: styles['actions-container'] }, actions)));
    function renderTabHeader(tab, index) {
        const { dismissible, dismissLabel, dismissDisabled, action, onDismiss } = tab;
        const isActive = activeTabId === tab.id && !tab.disabled;
        const clickTab = (event) => {
            var _a;
            if (tab.disabled) {
                event.preventDefault();
                return;
            }
            // if the primary mouse button is clicked with a modifier key, the browser will handle opening a new tab
            const specialKey = !isPlainLeftClick(event);
            if (specialKey && tab.href) {
                return;
            }
            event.preventDefault();
            // for browsers that do not focus buttons on button click
            if (!tab.href) {
                const clickedTabRef = tabRefs.current.get(tab.id);
                if (clickedTabRef) {
                    if (clickedTabRef && clickedTabRef !== ((_a = documentRef.current) === null || _a === void 0 ? void 0 : _a.activeElement)) {
                        clickedTabRef.focus({ preventScroll: true });
                    }
                }
            }
            if (tab.id === activeTabId) {
                return;
            }
            setFocusedTabId(tab.id);
            setPreviousActiveTabId(tab.id);
            onChange({ activeTabId: tab.id, activeTabHref: tab.href });
        };
        const classes = clsx({
            [styles['tabs-tab-link']]: true,
            [styles.refresh]: isVisualRefresh,
            [styles['tabs-tab-active']]: activeTabId === tab.id && !tab.disabled,
            [styles['tabs-tab-focused']]: focusedTabId === tab.id,
            [styles['tabs-tab-active']]: isActive,
            [analyticsSelectors['active-tab-header']]: isActive,
            [styles['tabs-tab-disabled']]: tab.disabled,
            [styles['tabs-tab-focusable']]: !tab.disabled || (tab.disabled && !!tab.disabledReason),
        });
        const tabHeaderContainerClasses = clsx({
            [styles['tabs-tab-header-container']]: true,
            [styles.refresh]: isVisualRefresh,
            [styles['tabs-tab-active']]: isActive,
            [styles['tabs-tab-disabled']]: tab.disabled,
            [styles['tabs-tab-focusable']]: !tab.disabled || (tab.disabled && !!tab.disabledReason),
        });
        const tabActionClasses = clsx({
            [styles['tabs-tab-action']]: true,
            [styles['tabs-tab-active']]: isActive,
        });
        const commonProps = {
            className: classes,
            'aria-controls': `${idNamespace}-${tab.id}-panel`,
            'data-testid': tab.id,
            id: getTabElementId({ namespace: idNamespace, tabId: tab.id }),
            onClick: clickTab,
        };
        const tabHeaderContainerAriaProps = hasActionOrDismissible
            ? {
                role: 'group',
                'aria-labelledby': commonProps.id,
            }
            : {};
        if (!hasActionOrDismissible) {
            commonProps['aria-selected'] = activeTabId === tab.id;
            commonProps.role = 'tab';
        }
        else {
            commonProps['aria-expanded'] = activeTabId === tab.id;
        }
        if (tab.disabled) {
            commonProps['aria-disabled'] = 'true';
        }
        const setElement = (tabElement) => {
            if (tab.id === activeTabId) {
                activeTabHeaderRef.current = tabElement;
            }
            tabRefs.current.set(tab.id, tabElement);
        };
        const handleDismiss = event => {
            if (!containerObjectRef.current || !onDismiss) {
                return;
            }
            const tabElements = getFocusablesFrom(containerObjectRef.current).filter(el => el.classList.contains(styles['tabs-tab-link']));
            const activeTabIndex = tabElements.findIndex(el => el.dataset.testid === tab.id);
            tabElements.splice(activeTabIndex, 1);
            let nextActive;
            if (previousActiveTabId && previousActiveTabId !== tab.id) {
                nextActive = tabElements.find(el => el.dataset.testid === previousActiveTabId);
            }
            else {
                nextActive = tabElements[Math.min(tabElements.length - 1, activeTabIndex)];
            }
            if (nextActive && nextActive.dataset.testid) {
                onChange({ activeTabId: nextActive.dataset.testid });
                setPreviousActiveTabId(nextActive.dataset.testid);
                nextActive.focus();
            }
            onDismiss(event);
        };
        const TabItem = hasActionOrDismissible ? 'div' : 'li';
        const analyticsDismissMetadata = {
            action: 'dismiss',
            detail: {
                id: tab.id,
                label: `.${analyticsSelectors['tab-dismiss-button']}`,
                position: `${index + 1}`,
            },
        };
        const analyticsComponentMetadataInnerContext = {
            innerContext: {
                tabId: tab.id,
                tabLabel: `.${analyticsSelectors['tab-label']}`,
                tabPosition: `${index + 1}`,
            },
        };
        return (React.createElement(TabItem, { ref: (element) => tabRefs.current.set(tab.id, element), className: styles['tabs-tab'], role: "presentation", key: tab.id },
            React.createElement("div", { ref: element => {
                    if (tab.id === activeTabId) {
                        activeHeaderContainerRef.current = element;
                    }
                }, className: tabHeaderContainerClasses, ...tabHeaderContainerAriaProps, ...getAnalyticsMetadataAttribute({ component: analyticsComponentMetadataInnerContext }), style: getTabContainerStyles(style) },
                React.createElement(TabTrigger, { ref: setElement, tab: tab, elementProps: commonProps, activeTabId: activeTabId, index: index, style: style }),
                action && React.createElement("span", { className: tabActionClasses }, action),
                dismissible && (React.createElement("span", { className: styles['tabs-tab-dismiss'], ...getAnalyticsMetadataAttribute(analyticsDismissMetadata) }, dismissButton({ dismissLabel, dismissDisabled, onDismiss: handleDismiss, tabId: tab.id }))))));
    }
}
const TabTrigger = forwardRef(({ tab, elementProps, activeTabId, index, style }, ref) => {
    const refObject = useRef(null);
    const tabLabelRefObject = useRef(null);
    const mergedRef = useMergeRefs(refObject, ref);
    const { tabIndex } = useSingleTabStopNavigation(refObject);
    const isDisabledWithReason = tab.disabled && !!tab.disabledReason;
    const [showTooltip, setShowTooltip] = useState(false);
    const { targetProps, descriptionEl } = useHiddenDescription(tab.disabledReason);
    const children = (React.createElement(React.Fragment, null,
        React.createElement("span", { className: clsx(styles['tabs-tab-label'], analyticsSelectors['tab-label']), ref: tabLabelRefObject },
            React.createElement("span", null, tab.label)),
        isDisabledWithReason && (React.createElement(React.Fragment, null,
            descriptionEl,
            showTooltip && (React.createElement(Tooltip, { className: styles['disabled-reason-tooltip'], getTrack: () => tabLabelRefObject.current, content: tab.disabledReason, onEscape: () => setShowTooltip(false) }))))));
    const handlers = {
        onFocus: () => setShowTooltip(true),
        onBlur: () => setShowTooltip(false),
        onMouseEnter: () => setShowTooltip(true),
        onMouseLeave: () => setShowTooltip(false),
    };
    const analyticsSelectMetadata = {
        action: 'select',
        detail: {
            id: tab.id,
            label: `.${analyticsSelectors['tab-label']}`,
            position: `${index + 1}`,
            originTabId: activeTabId || '',
        },
    };
    const commonProps = {
        ...elementProps,
        ...(isDisabledWithReason ? targetProps : {}),
        ...(isDisabledWithReason ? handlers : {}),
        ref: mergedRef,
        tabIndex: tabIndex,
        style: { ...elementProps.style, ...getTabStyles(style) },
        ...(tab.disabled || tab.id === activeTabId ? {} : getAnalyticsMetadataAttribute(analyticsSelectMetadata)),
    };
    return tab.href ? (React.createElement("a", { ...commonProps, href: tab.href, "data-awsui-motion-trigger": "hover" }, children)) : (React.createElement("button", { ...commonProps, type: "button", disabled: tab.disabled && !isDisabledWithReason, "data-awsui-motion-trigger": "hover" }, children));
});
export function getTabElementId({ namespace, tabId }) {
    return namespace + '-' + tabId;
}
//# sourceMappingURL=tab-header-bar.js.map